import { useCallback, useEffect, useState } from "react";
import * as api from "../api";

// «Mis tareas»: la lista agrupada que devuelve el servidor (GET /api/mis-tareas).
//
// estado: "cargando" | "ok" | "no-disponible" | "error".
//   · «no-disponible» = la funcion aun no esta abierta para esta persona (403) o el
//     servidor no la conoce (404). No es un fallo: Inicio no debe pintar ningun error.
//   · «error» = cualquier otra cosa (red, 500).

export const GRUPOS = [
  { clave: "vencidas",     etiqueta: "Vencidas",      abierto: true,  alerta: true },
  { clave: "hoy",          etiqueta: "Hoy",           abierto: true },
  { clave: "semana",       etiqueta: "Esta semana",   abierto: false },
  { clave: "mas_adelante", etiqueta: "Más adelante",  abierto: false },
  { clave: "sin_fecha",    etiqueta: "Sin fecha",     abierto: false },
  { clave: "aplazadas",    etiqueta: "Aplazadas",     abierto: false },
];

// Deja la respuesta con la forma esperada aunque falte algo: la pantalla nunca
// tiene que comprobar si un grupo existe.
const grupoPlan = x => {
  const items = Array.isArray(x?.items) ? x.items : [];
  return { total: Number.isFinite(x?.total) ? x.total : items.length, items };
};

// La vista «Plan»: hoy, un grupo por dia, esta semana y sin planificar. Null si el servidor
// no la trae (migracion sin aplicar): la pantalla usa entonces los grupos de siempre.
export function normalizaPlan(p) {
  if (!p) return null;
  return {
    tope: Number(p.tope_hoy) || 5,
    hoy: grupoPlan(p.hoy),
    dias: (Array.isArray(p.dias) ? p.dias : []).map(g => ({ ...grupoPlan(g), dia: g.dia, pasado: !!g.pasado })),
    semana: grupoPlan(p.semana),
    semana_siguiente: grupoPlan(p.semana_siguiente),
    sin_planificar: grupoPlan(p.sin_planificar),
    conteo: p.conteo && typeof p.conteo === "object" ? p.conteo : {},
  };
}

// Notion al dia: cuando se sincronizo por ultima vez y si hay una sincronizacion en marcha. Null si el servidor no lo dice.
export function normalizaNotion(n) {
  if (!n || typeof n !== "object") return null;
  return {
    conectado: !!n.conectado,
    ultima: typeof n.ultima_sincronizacion === "string" ? n.ultima_sincronizacion : null,   // ISO
    sincronizando: !!n.sincronizando,
    error: typeof n.ultimo_error === "string" ? n.ultimo_error : null,                       // sesion | limite | error
    reintentoEn: Number.isFinite(n.reintento_en) ? n.reintento_en : null,                   // segundos
  };
}

export function normalizaMisTareas(d) {
  const grupos = {};
  for (const g of GRUPOS) {
    const x = d?.grupos?.[g.clave];
    const items = Array.isArray(x?.items) ? x.items : [];
    grupos[g.clave] = { total: Number.isFinite(x?.total) ? x.total : items.length, items };
  }
  return {
    grupos,
    mi_dia: Array.isArray(d?.mi_dia) ? d.mi_dia.slice(0, 5) : [],
    hechas_total: Number(d?.hechas_total) || 0,
    plan: normalizaPlan(d?.plan),
    // Propuestas que me han hecho y aun no esta aprobadas: aparte de los grupos (no se planifican ni cuentan en la carga)
    propuestas: Array.isArray(d?.propuestas) ? d.propuestas : [],
    notion: normalizaNotion(d?.notion),
  };
}

export default function useMisTareas(activo = true) {
  const [datos, setDatos] = useState(null);
  const [estado, setEstado] = useState("cargando");

  const cargar = useCallback(async () => {
    try {
      const d = await api.getMisTareas();
      // Migracion sin aplicar: el servidor contesta 200 con la bandeja vacia y esta marca.
      // Decir «Todo al día» seria mentira: es como si aun no existiera.
      if (d?.migracion_pendiente) { setEstado("no-disponible"); return; }
      setDatos(normalizaMisTareas(d));
      setEstado("ok");
    } catch (e) {
      setEstado(e?.status === 403 || e?.status === 404 ? "no-disponible" : "error");
    }
  }, []);

  useEffect(() => { if (activo) cargar(); }, [activo, cargar]);

  // Una accion y se vuelve a pedir la lista: el servidor decide a que grupo va cada
  // tarea (aplazada, de hoy...), asi que mover filas en el cliente seria adivinar.
  // Devuelve la respuesta del servidor (puede traer `aviso`) o lanza el error.
  const actuar = useCallback(async (item, cambios) => {
    const r = await api.actuarMisTarea(item, cambios);
    await cargar();
    return r;
  }, [cargar]);

  // Planificar y el recordatorio de To Do: igual que actuar, el servidor decide donde cae la
  // tarea y se vuelve a pedir la lista. Los errores (DIA_LLENO...) se relanzan con su `code`.
  const planificar = useCallback(async (item, cuerpo) => {
    const r = await api.planificarMisTarea(item, cuerpo);
    await cargar();
    return r;
  }, [cargar]);
  const recordatorio = useCallback(async (id, cuerpo) => {
    const r = cuerpo === null ? await api.quitarRecordatorioMisTarea(id) : await api.ponerRecordatorioMisTarea(id, cuerpo);
    await cargar();
    return r;
  }, [cargar]);

  return { datos, estado, cargar, actuar, planificar, recordatorio };
}
